import React, {useEffect, useState, useCallback} from 'react';
import {useLocation} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {AddButton, CustomDelete, FullTable, SelectActions} from 'components';
import {ChangeTaskStatus, CreateTask, useTaskSocket} from './fragments';
import {tagActions, taskActions} from 'store';
import {Images, PermissionList, RolePermission, tagEnums, useModal} from 'utils';
import {TaskFilters, TaskSelectedFilters} from './taskFilters';
import {ACTION_TYPE, taskBody, taskHead, taskNotYet} from './constants';
import {ChangeTaskAssigned} from './fragments/changeTaskAssigned';

const DELETE_ACTION_TYPE = 'DELETE_TASK';
const BULK_DELETE_ACTION_TYPE = 'BULK_DELETE_TASKS';
export const Tasks = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const info = location?.state;
    const tasksList = useSelector((state) => state.tasks.tasksList);
    const userInfo = useSelector((state) => state.auth.userInfo);
    const {open} = useModal();
    const [selectedList, setSelectedList] = useState([]);

    useEffect(() => {
        dispatch(tagActions.getTags({module: tagEnums.TASK}));
    }, []);

    const renderParams = useCallback(() => {
        const filteredInfo = {
            ...info,
        };
        filteredInfo.limit = 50;
        filteredInfo.skip = info?.skip ? info?.skip : 0;

        if (info?.statuses?.length) {
            filteredInfo.statuses = info?.statuses;
        } else {
            delete filteredInfo.statuses;
        }

        delete filteredInfo.page;
        delete filteredInfo.reporterFull;
        delete filteredInfo.assigneeFull;

        return filteredInfo;
    }, [info]);

    useTaskSocket(renderParams());

    const fetchTasks = (load) => {
        dispatch(taskActions.getTasks(renderParams(), load));
    };

    useEffect(() => {
        fetchTasks();
    }, [info]);

    const handleActions = (item, name) => {
        if (name === 'edit') {
            open(<CreateTask afterSuccess={() => fetchTasks('noLoad')} info={item}/>);
        }
        if (name === 'delete') {
            open(
                <CustomDelete
                    text="Are you sure you want to delete this task? This action is not reversible."
                    info="Delete Task"
                    handleDel={() => dispatch(taskActions.deleteTask(item?.id))}
                    actionType={DELETE_ACTION_TYPE}
                    innerText={'Delete'}
                    afterSuccess={() => fetchTasks('noLoad')}
                />
            );
        }
    };

    const handleCheck = (ev, item, name) => {
        ev.stopPropagation();
        ev.preventDefault();

        if (name === 'all') {
            let allList = tasksList?.tasks?.length === selectedList?.length ? [] : tasksList?.tasks?.map((i) => i?.id);
            setSelectedList(allList);
        } else {
            const list = [...selectedList];
            const uniqSet = new Set(list);

            if (!uniqSet.has(item?.id)) {
                list.push(item?.id);
            } else {
                list.indexOf(item?.id) !== -1 && list.splice(list.indexOf(item?.id), 1);
            }
            setSelectedList(list);
        }
    };

    const resetSelected = () => {
        setSelectedList([]);
    };

    return (
        <div>
            {!!selectedList?.length && (
                <SelectActions
                    submitText={'Change Status'}
                    submitBtnIcon={Images.refreshWhite}
                    selectInnetText={selectedList?.length === tasksList?.tasks?.length ? 'Clear Selection' : ` Select all`}
                    selectedList={selectedList?.length}
                    selectAll={() => {
                        if (selectedList?.length === tasksList?.tasks?.length) {
                            resetSelected();
                        } else {
                            setSelectedList(tasksList?.tasks?.map((i) => i?.id));
                        }
                    }}
                    removeSelected={resetSelected}
                    submitClaims={() => {
                        open(
                            <ChangeTaskStatus
                                renderParams={renderParams()}
                                selectedIds={selectedList}
                                afterSuccess={() => {
                                    resetSelected();
                                }}
                            />
                        );
                    }}
                    renderOtherBtns={
                        <div className="flex-align-center" style={{height: 30, gap: 8}}>
                            <button
                                className="select-all-btn"
                                onClick={() => {
                                    open(
                                        <ChangeTaskAssigned
                                            renderParams={renderParams()}
                                            selectedIds={selectedList}
                                            afterSuccess={() => {
                                                resetSelected();
                                            }}
                                        />
                                    );
                                }}>
                                <img src={Images.atASign} alt="atASign"/>
                                <p>Assign</p>
                            </button>
                            <button
                                className="mass-delete"
                                onClick={() =>
                                    open(
                                        <CustomDelete
                                            text="Are you sure you want to delete these tasks? This action is not reversible."
                                            info="Delete Tasks"
                                            handleDel={() => dispatch(taskActions.bulkDeleteTasks({ids: selectedList}, renderParams()))}
                                            actionType={BULK_DELETE_ACTION_TYPE}
                                            innerText={'Delete'}
                                            afterSuccess={() => resetSelected()}
                                        />
                                    )
                                }>
                                <img src={Images.redTrashLine} alt="lock"/>
                            </button>
                        </div>
                    }
                />
            )}

            <div className="space-between-align-end">
                <TaskFilters/>
                <div>
                    {RolePermission([PermissionList.TASK_MANAGE?.code, PermissionList.TASK_FULL_ACCESS?.code]) && (
                        <AddButton
                            styles={{whiteSpace: 'nowrap'}}
                            text={'Create Task'}
                            handleClick={() => open(<CreateTask afterSuccess={() => fetchTasks('noLoad')}/>)}
                        />
                    )}
                </div>
            </div>

            <div style={{marginTop: 12}}>
                <TaskSelectedFilters/>
            </div>

            <FullTable
                head={taskHead(handleCheck, tasksList?.tasks, selectedList)}
                body={taskBody(handleCheck, tasksList?.tasks, selectedList, renderParams())}
                loadingType={ACTION_TYPE}
                list={tasksList?.tasks}
                listCount={tasksList?.count}
                handleClick={(id, item) => open(<CreateTask info={item} afterSuccess={() => fetchTasks('noLoad')}/>)}
                handleClickButton={handleActions}
                notYet={taskNotYet}
                selectedList={selectedList}
                margin={'12px 0 0 0'}
            />
        </div>
    );
};
